import { FC, useEffect, useState } from "react";
import * as yup from 'yup';
import { StockMovementService } from "../../../services/stockMovements/stockMovementService";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useProviders from "../../../hooks/api-calls/useProviders";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFormik } from "formik";
import { toast } from "sonner";
import CustomSelect from "../../../components/forms/CustomSelect";
import useProducts from "../../../hooks/api-calls/useProducts";
import RequiredInput from "../../../components/forms/InputHOC";
import { Textarea, Button } from "rizzui";
import { Product } from "../../../type/entities/product-type";
import ModalForm from "../../../components/forms/ModalForm";
import { HiOutlineInformationCircle } from "react-icons/hi2";

interface AddStockModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    product: Product;
    refetch: () => void;
}

const schema = yup.object({
    type: yup.string().required('Tipo es requerido'),
    productId: yup.string().required('Producto es requerido'),
    quantity: yup.number().required('Cantidad es requerida').min(1, 'Cantidad debe ser mayor a 0'),
    totalPrice: yup.number().required('Precio total es requerido').min(1, 'Precio total debe ser mayor a 0'),
    source: yup.string().required('Origen es requerido'),
    providerId: yup.string().nullable(),
    description: yup.string().nullable(),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const AddStockModal: FC<AddStockModalProps> = ({
    isOpen,
    setIsOpen,
    product,
    refetch,
}) => {

    // STATES

    const service = new StockMovementService();
    const [isSearched, setIsSearched] = useState(false);


    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getProvidersList } = useProviders();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            type: 'in',
            productId: product.id,
            quantity: 0,
            totalPrice: 0,
            source: '',
            providerId: '',
            description: '',
            company: product.company?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            handleCreate(values);
        }
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN MOVIMIENTO DE STOCK
     * @EN CREATE A STOCK MOVEMENT
     * 
     * @param values
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createStockMovement(values)).getResponseData();
            if (response.success) {
                refetch();
                setIsOpen(false);
                formik.resetForm();
                toast.success('Stock agregado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    // Resetear formulario cuando se abre el modal
    useEffect(() => {
        if (isOpen) {
            formik.resetForm();
            formik.setFieldValue('productId', product.id);
            formik.setFieldValue('company', product.company?.id ?? '');
            formik.setFieldValue('type', 'in');
        }
    }, [isOpen, product]);

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <ModalForm
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title={`Agregar Stock - ${product.name}`}
            containerClassName="!max-w-2xl"
        >
            <form onSubmit={formik.handleSubmit} className="p-2">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-6">
                    {userCan('list', 'company') && (
                        <div className="md:col-span-2">
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañía"
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('company', e?.value) }
                                }
                                error={formik.errors.company}
                            />
                        </div>
                    )}

                    <div className="md:col-span-2">
                        <CustomSelect
                            label="Proveedor"
                            id="providerId"
                            isSearchable
                            value={{ value: formik.values.providerId, label: getProvidersList()?.find((g: any) => g.value === formik.values.providerId)?.label }}
                            options={getProvidersList()}
                            onChange={(e: any) => { formik.setFieldValue('providerId', e?.value) }}
                            error={formik.touched.providerId && formik.errors.providerId}
                            display={formik.values.type === 'in'}
                        />
                    </div>

                    <RequiredInput
                        label="Cantidad"
                        id="quantity"
                        type="number"
                        placeholder="0"
                        value={formik.values.quantity}
                        onChange={(e: any) => { formik.setFieldValue('quantity', e.target.value) }}
                        error={formik.touched.quantity && formik.errors.quantity}
                    />

                    <RequiredInput
                        label="Precio Total del movimiento"
                        id="totalPrice"
                        type="number"
                        placeholder="0.00"
                        value={formik.values.totalPrice}
                        onChange={(e: any) => { 
                            formik.setFieldValue('totalPrice', e.target.value);
                        }}
                        error={formik.touched.totalPrice && formik.errors.totalPrice}
                    />

                    <div className="md:col-span-2">
                        <Textarea
                            label="Origen/Destino"
                            id="source"
                            rows={2}
                            placeholder="Ej. Almacén Central"
                            value={formik.values.source}
                            onChange={(e: any) => { formik.setFieldValue('source', e.target.value) }}
                            required
                            error={formik.errors.source}
                        />
                    </div>
                    
                    <div className="md:col-span-2">
                        <Textarea
                            label="Descripción (Opcional)"
                            id="description"
                            rows={3}
                            placeholder="Añade detalles adicionales sobre este movimiento..."
                            value={formik.values.description}
                            onChange={(e: any) => { formik.setFieldValue('description', e.target.value) }}
                        />
                    </div>
                </div>

                <div className="mt-8 flex items-center justify-end gap-3 border-t pt-6">
                    <Button
                        variant="outline"
                        onClick={() => setIsOpen(false)}
                        type="button"
                        className="px-6"
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        isLoading={formik.isSubmitting}
                        disabled={formik.isSubmitting}
                        className="px-6"
                    >
                        Agregar Stock
                    </Button>
                </div>
            </form>
        </ModalForm>
    );
};

export default AddStockModal;